/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import type {
  DeploymentHealth,
  SaasUserResponse,
  DeploymentGetResponse,
  DeploymentTemplateInfoV2,
  Organization,
} from '@modules/cloud-api/v1/types'
import type { ElasticsearchCluster, AsyncRequestState } from '@modules/ui-types'
import type { EolStatusResponse } from '@modules/ui-types/eolStatus'
import { withSmallErrorBoundary } from '@modules/cui/Boundary'
import Feature from '@modules/utils/feature'
import withPermissions from '@modules/permissions-components/PermissionsGate/withPermissions'

import { getDeploymentTemplateId } from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { getDeploymentHealth } from '@/reducers/deploymentHealth'
import { getEolStatus } from '@/reducers/eolStatus'
import { getStackDeployment } from '@/reducers/stackDeployments'
import { getSaasUser } from '@/reducers/saasUsers'
import { getOrganization } from '@/reducers/organizations'
import { getDeploymentTemplate } from '@/reducers/deploymentTemplates'
import { getCluster } from '@/reducers/clusters'
import { fetchDeploymentHealthRequest } from '@/reducers/asyncRequests/registry'

import { fetchDeploymentTemplates } from '../../actions/topology/deploymentTemplates'
import { isFeatureActivated, getConfigForKey } from '../../selectors'
import { getRouteParams } from '../../lib/router'
import { withStackDeploymentRouteParams } from '../StackDeploymentEditor'

import ChromeNavigation from './ChromeNavigation'

import type { ReduxState } from '@/types/redux'
import type { WithStackDeploymentRouteParamsProps } from '../StackDeploymentEditor'
import type { RouteConfig } from 'react-router-config'

type StateProps = {
  deployment?: ElasticsearchCluster | null
  deploymentTemplate?: DeploymentTemplateInfoV2
  stackDeployment?: DeploymentGetResponse | null
  defaultRegionId?: string
  regionId?: string
  deploymentId?: string
  stackDeploymentId?: string
  saasUserProfile?: SaasUserResponse
  organization?: Organization
  lookupSaasUsers: boolean
  manageRbacFeature: boolean
  trafficFilteringFeature: boolean
  serverlessFeature: boolean
  showUserApiKeys: boolean
  eolStatus: EolStatusResponse | null
  deploymentHealthRequest: AsyncRequestState
  health: DeploymentHealth | null
}

type DispatchProps = {
  fetchDeploymentTemplates: (params: {
    regionId: string
    stackVersion: string
    showMaxZones: boolean
  }) => void
}

type OwnProps = {
  routes: RouteConfig[]
}

type ConsumerProps = WithStackDeploymentRouteParamsProps & OwnProps

type RouteParams = { userId: string; organizationId: string; templateId: string }

const mapStateToProps = (
  state: ReduxState,
  { regionId: regionIdParam, deploymentId, stackDeploymentId, routes, location }: ConsumerProps,
): StateProps => {
  const params = getRouteParams<RouteParams>(routes, location.pathname)
  const { userId, organizationId } = params
  const defaultRegionId = getConfigForKey(state, `DEFAULT_REGION`)
  const regionId = regionIdParam || defaultRegionId

  const manageRbacFeature = isFeatureActivated(state, Feature.manageRbac)
  const serverlessFeature = isFeatureActivated(state, Feature.serverless)
  const stackDeployment = getStackDeployment(state, stackDeploymentId)
  const deployment = getCluster(state, regionId, deploymentId!)

  const deploymentTemplate = resolveDeploymentTemplate({
    state,
    params,
    deployment,
    regionId,
    stackDeployment,
  })

  const isAnySaas = getConfigForKey(state, `APP_PLATFORM`) === `saas`

  return {
    regionId,
    deployment,
    deploymentTemplate,
    defaultRegionId,
    trafficFilteringFeature: isFeatureActivated(state, Feature.trafficFiltering),
    manageRbacFeature,
    serverlessFeature,
    lookupSaasUsers: isFeatureActivated(state, Feature.lookupSaasUsers),
    saasUserProfile: getSaasUser(state, userId!),
    organization: getOrganization(state, organizationId!),
    showUserApiKeys: isAnySaas,
    stackDeployment,
    eolStatus: getEolStatus(state),
    health: getDeploymentHealth(state, stackDeploymentId!),
    deploymentHealthRequest: fetchDeploymentHealthRequest(state, stackDeploymentId!),
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchDeploymentTemplates,
}

export default withSmallErrorBoundary(
  withStackDeploymentRouteParams<OwnProps>(
    connect<StateProps, DispatchProps, ConsumerProps>(
      mapStateToProps,
      mapDispatchToProps,
    )(
      withPermissions({
        hasSearchDeploymentsPermission: {
          permissionsToCheck: [{ type: 'deployment', action: 'search' }],
        },
        hasSearchAllocatorsPermission: {
          permissionsToCheck: [{ type: 'allocator', action: 'search' }],
        },
        hasListApiKeysPermission: {
          permissionsToCheck: [{ type: 'api-key', action: 'list' }],
        },
        hasListUsersPermission: {
          permissionsToCheck: [{ type: 'admin-user', action: 'list' }],
        },
      })(ChromeNavigation),
    ),
  ),
)

function resolveDeploymentTemplate({
  state,
  params,
  deployment,
  regionId,
  stackDeployment,
}: {
  state: ReduxState
  params: Partial<RouteParams>
  deployment?: ElasticsearchCluster | null
  stackDeployment?: DeploymentGetResponse | null
  regionId?: string
}) {
  // navigation inside a deployment, so use the template ID referenced in the deployment
  if (stackDeployment) {
    const deploymentTemplateId = getDeploymentTemplateId({ deployment: stackDeployment })
    const version = getVersion({ deployment: stackDeployment })

    return getDeploymentTemplate(state, regionId!, deploymentTemplateId!, version)
  }

  if (deployment && deployment.plan) {
    return getDeploymentTemplate(
      state,
      regionId!,
      deployment.deploymentTemplateId!,
      deployment.plan.version,
    )
  }

  const { templateId } = params

  // navigation in deployment template CRUD area, so use the template ID from url params
  if (regionId && templateId) {
    return getDeploymentTemplate(state, regionId, templateId)
  }

  return
}
